import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Search, Filter, Download, Eye, Edit, 
  UserX, UserCheck, ChevronLeft, ChevronRight,
  MoreVertical, RefreshCw, Mail, Shield,
  AlertCircle, Calendar, DollarSign,
  ArrowUpDown, CheckCircle, XCircle, Clock,
  Activity, TrendingUp, UserPlus
} from 'lucide-react';
import { AdminUsersAPI, AdminAPIUtils } from '../services/api';

function UsersPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [kycFilter, setKycFilter] = useState('all');
  const [actionMenu, setActionMenu] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    newToday: 0,
    kycPending: 0
  });

  useEffect(() => {
    fetchUsers();
    fetchUserStats();
  }, [pagination.page, statusFilter, kycFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      if (kycFilter !== 'all') {
        params.kyc_status = kycFilter;
      }

      if (searchTerm) {
        params.search = searchTerm;
      }

      const response = await AdminUsersAPI.getUsers(params);
      setUsers(response.users || []);
      setPagination({
        page: response.pagination?.page || 1,
        limit: response.pagination?.limit || 20,
        total: response.pagination?.total || 0,
        totalPages: response.pagination?.pages || 1
      });
    } catch (err) {
      setError(AdminAPIUtils.handleError(err, false).message);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const response = await AdminUsersAPI.getUserStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch user stats:', err);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchUsers();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setKycFilter('all');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const updateUserStatus = async (userId, status) => {
    if (!window.confirm(`Are you sure you want to ${status} this user?`)) return;

    try {
      await AdminUsersAPI.updateUserStatus(userId, status);
      fetchUsers(); // Refresh the list
    } catch (err) {
      AdminAPIUtils.handleError(err);
    }
  };

  const exportUsers = async () => {
    try {
      await AdminUsersAPI.exportUsers({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        kyc_status: kycFilter !== 'all' ? kycFilter : undefined,
        search: searchTerm || undefined
      });
    } catch (err) {
      AdminAPIUtils.handleError(err);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
      pending: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' },
      suspended: { bg: 'bg-rose-500/10', text: 'text-rose-400', border: 'border-rose-500/20' },
      banned: { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20' }
    };
    
    const configItem = config[status] || config.banned;
    
    return (
      <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${configItem.bg} ${configItem.text} border ${configItem.border} flex items-center gap-1.5`}>
        {status === 'active' && <CheckCircle className="w-3 h-3" />}
        {status === 'pending' && <Clock className="w-3 h-3" />}
        {status === 'suspended' && <UserX className="w-3 h-3" />}
        {status === 'banned' && <XCircle className="w-3 h-3" />}
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const getKycBadge = (status) => {
    const config = {
      verified: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' },
      pending: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' },
      rejected: { bg: 'bg-rose-500/10', text: 'text-rose-400', border: 'border-rose-500/20' },
      not_submitted: { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20' }
    };
    
    const configItem = config[status] || config.not_submitted;
    const displayText = status?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    
    return (
      <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${configItem.bg} ${configItem.text} border ${configItem.border}`}>
        {displayText}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.charAt(0).toUpperCase();
  };

  const statCards = [
    {
      title: 'Total Users',
      value: stats.total || 0,
      icon: <UserPlus className="w-5 h-5" />,
      color: 'from-blue-500 to-cyan-500',
      change: stats.newToday || 0,
      changeText: 'new today',
      trend: 'up'
    },
    {
      title: 'Active Users',
      value: stats.active || 0,
      icon: <Activity className="w-5 h-5" />,
      color: 'from-emerald-500 to-green-500',
      change: '12.5%',
      changeText: 'from last week',
      trend: 'up'
    },
    {
      title: 'KYC Pending',
      value: stats.kycPending || 0,
      icon: <Shield className="w-5 h-5" />,
      color: 'from-amber-500 to-yellow-500',
      change: '3',
      changeText: 'new submissions',
      trend: 'neutral'
    },
    {
      title: 'New Today',
      value: stats.newToday || 0,
      icon: <UserPlus className="w-5 h-5" />,
      color: 'from-violet-500 to-purple-500',
      change: '24%',
      changeText: 'growth rate',
      trend: 'up'
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent">
            User Management
          </h1>
          <p className="text-charcoal-gray mt-1">Manage and monitor all user accounts and activities</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={exportUsers}
            className="px-4 py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={fetchUsers}
            className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:scale-[1.02]"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, index) => (
          <div 
            key={index} 
            className="bg-night-blue/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 hover:bg-night-blue/70 transition-all duration-300 hover:scale-[1.02] group"
          >
            <div className="flex items-start justify-between mb-6">
              <div className={`p-3 rounded-xl bg-gradient-to-br ${card.color} shadow-lg`}>
                {card.icon}
              </div>
              {card.trend === 'up' && (
                <div className="flex items-center text-emerald-400 bg-emerald-400/10 px-3 py-1 rounded-full">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  <span className="text-sm font-medium">+{card.change}</span>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <p className="text-3xl font-bold text-white">{card.value}</p>
              <p className="text-charcoal-gray text-sm uppercase tracking-wider">{card.title}</p>
            </div>
            <div className="mt-6 pt-4 border-t border-gray-800">
              <p className="text-sm text-charcoal-gray">
                <span className={`font-medium ${card.trend === 'up' ? 'text-emerald-400' : 'text-gray-400'}`}>
                  {card.trend === 'up' ? '+' : ''}{card.change}
                </span>{' '}
                {card.changeText}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters Card */}
      <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-charcoal-gray w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search by username, email, or name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white placeholder-charcoal-gray focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-all duration-300"
                  />
                </div>
              </div>
              <button
                type="submit"
                className="px-5 py-3 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300 hover:scale-[1.02]"
              >
                Search
              </button>
            </form>
          </div>

          {/* Status Filter */}
          <div>
            <div className="flex items-center gap-3">
              <Filter className="text-charcoal-gray w-5 h-5" />
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
              >
                <option value="all">All Status</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
                <option value="2">Suspended</option>
                <option value="3">Banned</option>
              </select>
            </div>
          </div>

          {/* KYC Filter */}
          <div>
            <div className="flex items-center gap-3">
              <Shield className="text-charcoal-gray w-5 h-5" />
              <select
                value={kycFilter}
                onChange={(e) => {
                  setKycFilter(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
              >
                <option value="all">All KYC Status</option>
                <option value="verified">Verified</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
                <option value="not_submitted">Not Submitted</option>
              </select>
            </div>
          </div>
        </div>

        {/* Filter Actions */}
        <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-800">
          <div className="text-sm text-charcoal-gray">
            Showing <span className="text-white font-medium">{users.length}</span> of{' '}
            <span className="text-white font-medium">{pagination.total}</span> users
          </div>
          <button
            onClick={clearFilters}
            className="text-sm text-charcoal-gray hover:text-white transition-colors hover:bg-gray-800/50 px-3 py-1.5 rounded-lg"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-sky-500 mx-auto mb-6"></div>
              <p className="text-charcoal-gray">Loading users...</p>
            </div>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-rose-400 mx-auto mb-4" />
            <div className="text-rose-300 mb-4 font-medium">Error: {error}</div>
            <button
              onClick={fetchUsers}
              className="px-5 py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300"
            >
              Retry
            </button>
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center">
            <UserPlus className="w-16 h-16 text-gray-700 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No users found</h3>
            <p className="text-charcoal-gray mb-6">Try adjusting your search or filters</p>
            <button
              onClick={clearFilters}
              className="px-5 py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-800">
                <thead className="bg-gray-900/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <UserPlus className="w-4 h-4" />
                        User
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <Activity className="w-4 h-4" />
                        Status
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        KYC
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4" />
                        Balance
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Joined
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {users.map((user) => (
                    <tr 
                      key={user.id} 
                      className="hover:bg-gray-800/30 transition-colors group"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="relative">
                            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                              {getInitials(user.username)}
                            </div>
                            <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-night-blue ${
                              user.status === 1 ? 'bg-emerald-500' : 
                              user.status === 2 ? 'bg-amber-500' : 
                              'bg-gray-500'
                            }`} />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-white group-hover:text-sky-300 transition-colors">
                              {user.username}
                            </div>
                            <div className="text-sm text-charcoal-gray flex items-center gap-2">
                              <Mail className="w-3 h-3" />
                              <span className="truncate max-w-[180px]">{user.email}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(user.status === 1 ? 'active' : user.status === 2 ? 'suspended' : user.status === 3 ? 'banned' : 'pending')}
                      </td>
                      <td className="px-6 py-4">
                        {getKycBadge(user.kyc_status)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <div className="text-lg font-bold text-emerald-400">
                            BTC{AdminAPIUtils.formatBTC(user.wallet_balance || 0)}
                          </div>
                          <div className="text-xs text-charcoal-gray">
                            BTC{AdminAPIUtils.formatBTC(user.total_deposits || 0)} deposited
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-white">{formatDate(user.created_at)}</div>
                        <div className="text-xs text-charcoal-gray">
                          {user.last_login ? formatDate(user.last_login) : 'Never logged in'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => navigate(`/users/${user.id}`)}
                            className="p-2 bg-gray-800/50 hover:bg-sky-500/20 text-sky-400 hover:text-sky-300 rounded-lg transition-all duration-300 hover:scale-110"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => navigate(`/users/${user.id}?tab=edit`)}
                            className="p-2 bg-gray-800/50 hover:bg-emerald-500/20 text-emerald-400 hover:text-emerald-300 rounded-lg transition-all duration-300 hover:scale-110"
                            title="Edit User"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <div className="relative">
                            <button
                              onClick={() => setActionMenu(actionMenu === user.id ? null : user.id)}
                              className="p-2 bg-gray-800/50 hover:bg-gray-700 text-gray-400 hover:text-white rounded-lg transition-all duration-300 hover:scale-110"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                            {actionMenu === user.id && (
                              <div className="absolute right-0 mt-2 w-48 bg-night-blue border border-gray-800 rounded-xl shadow-2xl py-2 z-50">
                                <div className="px-4 py-2 border-b border-gray-800">
                                  <p className="text-sm font-medium text-white">Quick Actions</p>
                                </div>
                                {user.status === 1 ? (
                                  <button
                                    onClick={() => {
                                      updateUserStatus(user.id, 'suspended');
                                      setActionMenu(null);
                                    }}
                                    className="w-full px-4 py-2.5 text-sm text-amber-400 hover:bg-amber-500/10 flex items-center gap-2 transition-colors"
                                  >
                                    <UserX className="w-4 h-4" />
                                    Suspend User
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => {
                                      updateUserStatus(user.id, 'active');
                                      setActionMenu(null);
                                    }}
                                    className="w-full px-4 py-2.5 text-sm text-emerald-400 hover:bg-emerald-500/10 flex items-center gap-2 transition-colors"
                                  >
                                    <UserCheck className="w-4 h-4" />
                                    Activate User
                                  </button>
                                )}
                                <button 
                                  className="w-full px-4 py-2.5 text-sm text-rose-400 hover:bg-rose-500/10 flex items-center gap-2 transition-colors"
                                  onClick={() => {
                                    if (window.confirm('Are you sure you want to ban this user?')) {
                                      updateUserStatus(user.id, 'banned');
                                      setActionMenu(null);
                                    }
                                  }}
                                >
                                  <UserX className="w-4 h-4" />
                                  Ban User
                                </button>
                                <div className="px-4 py-2 border-t border-gray-800">
                                  <p className="text-xs text-charcoal-gray">User ID: {user.id}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-800">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-charcoal-gray">
                    Page <span className="font-medium text-white">{pagination.page}</span> of{' '}
                    <span className="font-medium text-white">{pagination.totalPages}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                      disabled={pagination.page === 1}
                      className={`px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all duration-300 ${
                        pagination.page === 1
                          ? 'bg-gray-800/50 text-gray-600 cursor-not-allowed'
                          : 'bg-gray-800 hover:bg-gray-700 text-white hover:scale-105'
                      }`}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </button>
                    <div className="flex items-center space-x-1">
                      {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                        let pageNum;
                        if (pagination.totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.page <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.page >= pagination.totalPages - 2) {
                          pageNum = pagination.totalPages - 4 + i;
                        } else {
                          pageNum = pagination.page - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                            className={`px-4 py-2.5 min-w-[40px] rounded-xl transition-all duration-300 ${
                              pagination.page === pageNum
                                ? 'bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-lg'
                                : 'bg-gray-800 hover:bg-gray-700 text-white hover:scale-105'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      {pagination.totalPages > 5 && pagination.page < pagination.totalPages - 2 && (
                        <>
                          <span className="px-2 text-charcoal-gray">...</span>
                          <button
                            onClick={() => setPagination(prev => ({ ...prev, page: pagination.totalPages }))}
                            className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl hover:scale-105 transition-all duration-300"
                          >
                            {pagination.totalPages}
                          </button>
                        </>
                      )}
                    </div>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.min(pagination.totalPages, prev.page + 1) }))}
                      disabled={pagination.page === pagination.totalPages}
                      className={`px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all duration-300 ${
                        pagination.page === pagination.totalPages
                          ? 'bg-gray-800/50 text-gray-600 cursor-not-allowed'
                          : 'bg-gray-800 hover:bg-gray-700 text-white hover:scale-105'
                      }`}
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default UsersPage;